import { useState } from "react";
import { Link } from "react-router-dom";

interface MobileTableCardsProps<T> {
  items: T[];
  keyExtractor: (item: T) => string;
  renderCard: (item: T) => React.ReactNode;
  renderActions?: (item: T) => React.ReactNode;
  emptyMessage?: string;
  onSwipeLeft?: (item: T) => void;
  swipeLeftLabel?: string;
  onSwipeRight?: (item: T) => void;
  swipeRightLabel?: string;
}

export default function MobileTableCards<T>({
  items,
  keyExtractor,
  renderCard,
  renderActions,
  emptyMessage = "Keine Einträge gefunden.",
  onSwipeLeft,
  swipeLeftLabel = "Löschen",
  onSwipeRight,
  swipeRightLabel = "Bearbeiten",
}: MobileTableCardsProps<T>) {
  const [swipedItem, setSwipedItem] = useState<string | null>(null);
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);

  const handleTouchStart = (e: React.TouchEvent, id: string) => {
    setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    setSwipedItem(null);
  };

  const handleTouchEnd = (e: React.TouchEvent, item: T) => {
    if (!touchStart) return;
    const dx = e.changedTouches[0].clientX - touchStart.x;
    const dy = e.changedTouches[0].clientY - touchStart.y;

    // Only trigger if horizontal swipe is dominant and significant
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 60) {
      const id = keyExtractor(item);
      if (dx < 0 && onSwipeLeft) {
        setSwipedItem(id);
      } else if (dx > 0 && onSwipeRight) {
        onSwipeRight(item);
      }
    }
    setTouchStart(null);
  };

  if (items.length === 0) {
    return (
      <div className="text-center py-10">
        <i className="ri-inbox-line text-4xl text-hj-gray-border mb-3 block"></i>
        <p className="text-sm text-hj-gray font-body">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item) => {
        const id = keyExtractor(item);
        const isSwiped = swipedItem === id;

        return (
          <div
            key={id}
            className="relative overflow-hidden rounded-lg border border-hj-gray-border bg-white"
            onTouchStart={(e) => handleTouchStart(e, id)}
            onTouchEnd={(e) => handleTouchEnd(e, item)}
          >
            {/* Swipe action background */}
            {onSwipeLeft && (
              <div
                className={`absolute inset-y-0 right-0 w-24 bg-red-500 flex items-center justify-center transition-opacity ${
                  isSwiped ? "opacity-100" : "opacity-0"
                }`}
              >
                <span className="text-white text-xs font-medium">{swipeLeftLabel}</span>
              </div>
            )}

            {/* Card content */}
            <div
              className={`relative bg-white transition-transform duration-200 ${
                isSwiped ? "-translate-x-24" : "translate-x-0"
              }`}
            >
              <div className="p-4">
                {renderCard(item)}

                {/* Actions row */}
                {renderActions && (
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-hj-gray-border">
                    {renderActions(item)}
                  </div>
                )}
              </div>

              {/* Swipe hint */}
              {!isSwiped && (onSwipeLeft || onSwipeRight) && (
                <div className="px-4 pb-2 flex items-center justify-center">
                  <span className="text-[10px] text-hj-gray/50 flex items-center gap-1">
                    <i className="ri-arrow-left-right-line"></i>
                    Wischen für Aktionen
                  </span>
                </div>
              )}
            </div>

            {/* Swipe confirm buttons */}
            {isSwiped && onSwipeLeft && (
              <div className="absolute inset-y-0 right-0 w-24 flex flex-col">
                <button
                  onClick={() => {
                    onSwipeLeft(item);
                    setSwipedItem(null);
                  }}
                  className="flex-1 flex items-center justify-center bg-red-500 text-white text-xs font-medium"
                >
                  <i className="ri-check-line mr-1"></i>
                  {swipeLeftLabel}
                </button>
                <button
                  onClick={() => setSwipedItem(null)}
                  className="flex-1 flex items-center justify-center bg-hj-dark text-white text-xs font-medium"
                >
                  <i className="ri-close-line mr-1"></i>
                  Abbrechen
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
